import { setCookie, getCookie } from '@/shared/lib/formatter'
import { createPersistedStore } from '@/shared/stores/create-persisted-store'

const VALID_LANGUAGES = ['uzCYR', 'uzLAT', 'ruRUS'] as const
type Language = (typeof VALID_LANGUAGES)[number]

export interface LanguageState {
  lang: string
  setLanguage: (lang: string) => void
  resetLanguage: () => void
  toggleLanguage: () => void
}

const DEFAULT_LANGUAGE: Language = 'uzCYR'

/** One-shot legacy cookie read so users keep their language across the
 *  cookie → localStorage migration. After ~30 days post-Wave-E, drop the
 *  cookie mirror in setLanguage. */
function readLegacyCookieLang(): Language {
  const cookie = getCookie('lang') as string | undefined
  if (cookie && (VALID_LANGUAGES as readonly string[]).includes(cookie)) {
    return cookie as Language
  }
  return DEFAULT_LANGUAGE
}

export const useLanguageStore = createPersistedStore<LanguageState, Pick<LanguageState, 'lang'>>(
  (set) => ({
    lang: readLegacyCookieLang(),

    setLanguage: (lang: string) =>
      set((state) => {
        if (VALID_LANGUAGES.includes(lang as Language)) {
          setCookie('lang', lang) // mirror for legacy readers; remove later
          state.lang = lang
        }
      }),

    resetLanguage: () =>
      set((state) => {
        setCookie('lang', DEFAULT_LANGUAGE)
        state.lang = DEFAULT_LANGUAGE
      }),

    toggleLanguage: () =>
      set((state) => {
        const next: Language = state.lang === 'uzCYR' ? 'uzLAT' : 'uzCYR'
        setCookie('lang', next)
        state.lang = next
      }),
  }),
  {
    name: 'glasspro:language',
    version: 1,
    partialize: (state) => ({ lang: state.lang }),
  },
)
